/**
 * App-side GTM dataLayer writer
 *
 * The single home for every app-side `window.dataLayer` write
 * (`app_page_view`, `sign_up` / `login`, `user_id`).
 *
 * - No-op without a container: when `VITE_PUBLIC_GTM_CONTAINER_ID` is unset,
 *   empty or the `off` sentinel, nothing is created or pushed.
 * - Writes are NOT consent-gated: Google Consent Mode governs which tags fire
 *   on these events, so the events themselves are always pushed.
 * - gtag `arguments` commands (consent default / update) belong to
 *   `consent/gtm.ts`, which reuses `ensureDataLayer()` for the same gate.
 */

import { readStashedUtm, stashUtmParams } from "../utils/utm";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Auth-handoff params that must never reach GA4 (`t` carries the JWT). */
const AUTH_URL_PARAMS = ["t", "auth_event", "auth_method"];

/** Auth events pushed after a successful sign-up or login redirect. */
export type AuthEventName = "sign_up" | "login";

/**
 * Get the GTM container id from the build-time Vite env var, read at call
 * time.
 *
 * @returns the container id, or null when unset, empty or `off`
 */
export function getGtmContainerId(): string | null {
  const raw = (
    import.meta.env.VITE_PUBLIC_GTM_CONTAINER_ID as string | undefined
  )?.trim();
  return !raw || raw === "off" ? null : raw;
}

/**
 * Get `window.dataLayer`, creating it when a container is configured.
 *
 * @returns the dataLayer array, or null when there is no container
 */
export function ensureDataLayer(): unknown[] | null {
  if (!getGtmContainerId()) {
    return null;
  }
  window.dataLayer ||= [];
  return window.dataLayer;
}

/**
 * Push one plain-object entry, or nothing when there is no container.
 *
 * @param entry - the dataLayer entry
 */
function push(entry: Record<string, unknown>): void {
  ensureDataLayer()?.push(entry);
}

/**
 * The current URL without the auth-handoff params. The first SPA pageview can
 * run before AuthContext strips `?t=<jwt>`, so the JWT is removed here.
 *
 * @returns `location.href`, untouched when no auth param is present
 */
function currentPageLocation(): string {
  const url = new URL(window.location.href);
  if (!AUTH_URL_PARAMS.some((key) => url.searchParams.has(key))) {
    return window.location.href;
  }
  for (const key of AUTH_URL_PARAMS) {
    url.searchParams.delete(key);
  }
  return url.toString();
}

/**
 * Push `app_page_view` for the current page, with the stashed UTMs (only the
 * keys that were stashed). `page_location` never carries the auth params.
 */
export function pushAppPageView(): void {
  push({
    event: "app_page_view",
    page_location: currentPageLocation(),
    page_title: document.title,
    page_referrer: document.referrer,
    ...(readStashedUtm() ?? {}),
  });
}

/**
 * Push an auth event.
 *
 * @param event - `sign_up` or `login`
 * @param method - the auth method (e.g. `email`, `google_oauth2`)
 */
export function pushAuthEvent(event: AuthEventName, method: string): void {
  push({ event, method });
}

/**
 * Push the signed-in user's id, or null after logout.
 *
 * @param id - the user uuid, or null to clear it
 */
export function pushUserId(id: string | null): void {
  push({ user_id: id });
}

/**
 * Stash the landing URL's UTMs for `app_page_view`, only when a container is
 * configured. A URL without UTMs never overwrites an earlier stash. PostHog
 * keeps its own stash inside `initPostHog()`.
 */
export function stashLandingUtm(): void {
  if (getGtmContainerId()) {
    stashUtmParams(window.location.search);
  }
}
